import { TransportItemType, getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import * as ct from 'countries-and-timezones';

import { FeatureNameEnum, getFeatureVariant } from 'hooks/useFeatureFlags';
import { getRequiredStringEnv } from 'utils/safeEnvParsing';

import { ConsoleInstrumentation } from './ConsoleInstrumentation';

export enum FaroEventNameEnum {
  UNIQUE_VISIT = 'unique_visit',
  TOTAL_VISIT = 'total_visit',
  DEVICE_SPECS = 'device_specs'
}

export const BYPASS_SAMPLE_RATE_EVENTS = [FaroEventNameEnum.DEVICE_SPECS];
const IGNORED_EVENTS = ['faro.performance.resource'];

export const DUMMY_SESSION_ID = 'anonymous';

// Map TransportItemType to corresponding feature flag
const TRANSPORT_TYPE_TO_FEATURE_FLAG = {
  [TransportItemType.EXCEPTION]: FeatureNameEnum.WEB_APP_OBSERVABILITY_REPORT_EXCEPTIONS,
  [TransportItemType.MEASUREMENT]: FeatureNameEnum.WEB_APP_OBSERVABILITY_REPORT_MEASUREMENTS,
  [TransportItemType.TRACE]: FeatureNameEnum.WEB_APP_OBSERVABILITY_REPORT_TRACES,
  [TransportItemType.EVENT]: FeatureNameEnum.WEB_APP_OBSERVABILITY_REPORT_EVENTS,
  [TransportItemType.LOG]: FeatureNameEnum.WEB_APP_OBSERVABILITY_REPORT_LOGS
} as const;

// Check if a specific telemetry type should be sampled
export const shouldReportType = async (type: TransportItemType): Promise<boolean> => {
  const featureFlag = TRANSPORT_TYPE_TO_FEATURE_FLAG[type];

  if (!featureFlag) {
    // Default behavior for unknown types: report exceptions, drop everything else.
    return type === TransportItemType.EXCEPTION;
  }

  try {
    const shouldReport = await getFeatureVariant(featureFlag);
    return shouldReport;
  } catch (error) {
    console.error(`Error getting sampling decision for ${type}:`, error);
  }

  // Default to false for any other value or if there was an error
  return false;
};

export const initFaro = async (collectorUrl: string, { isolate = false }: { isolate?: boolean }) => {
  try {
    // Get sampling decisions for each telemetry type
    const shouldReportMap = {
      [TransportItemType.EXCEPTION]: await shouldReportType(TransportItemType.EXCEPTION),
      [TransportItemType.MEASUREMENT]: await shouldReportType(TransportItemType.MEASUREMENT),
      [TransportItemType.TRACE]: await shouldReportType(TransportItemType.TRACE),
      [TransportItemType.EVENT]: await shouldReportType(TransportItemType.EVENT),
      [TransportItemType.LOG]: await shouldReportType(TransportItemType.LOG)
    };

    const instrumentations = getWebInstrumentations({
      captureConsole: false,
      enablePerformanceInstrumentation: true
    });

    const faro = initializeFaro({
      url: collectorUrl,
      instrumentations: [...instrumentations, new ConsoleInstrumentation()],
      app: {
        name: 'Speechify Web App',
        version: getRequiredStringEnv('version')
      },
      isolate,
      beforeSend: event => {
        if (event.meta.session) {
          event.meta.session.id = DUMMY_SESSION_ID;

          if (event.meta.session.attributes) {
            event.meta.session.attributes.previousSession = DUMMY_SESSION_ID;
            event.meta.session.attributes.country = ct.getCountryForTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)?.id || '--';
          }
        }

        // Special case for events that should bypass sampling
        if (event.type === 'event' && BYPASS_SAMPLE_RATE_EVENTS.includes((event.payload as { name: string }).name as FaroEventNameEnum)) {
          return event;
        }

        if (IGNORED_EVENTS.includes(event.type)) {
          return null;
        }

        // Check if this event type should be sampled based on feature flags
        const shouldReport = shouldReportMap[event.type as TransportItemType];

        // If we have a specific sampling decision for this type, use it
        if (shouldReport) {
          return event; // Send the event if reporting is enabled for this type
        }

        if (event.type === 'log' && ['openUpsellModal', 'showUpsellModal'].includes((event.payload as { message: string })?.message)) {
          return event;
        }

        return null; // Drop the event if reporting is disabled for this type
      }
    });

    faro.api.pushLog(['Faro initialized']);
    return faro;
  } catch (err) {
    console.error('Error initializing Faro:', err);
    return undefined;
  }
};
